/* eslint-disable no-case-declarations */
import { matchPath, type To } from "react-router";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { getCurrentRouteWithoutBasename } from "@/app-utils/route.ts";
import {
  type ColumnAnnotation,
  type CoordinateList,
  type GenomeViewState,
  type Group,
  type InputBlastViewStateInput,
  type InputColumnView,
  type InputTableQuerySpec,
  type InputTableViewStateInput,
  type InputTrack,
  type InputUserParamsInput,
  type InputViewInput,
  type InputViewStateInput,
  type Parent,
  type PlaybookEvent,
  type Project,
  type RegionFlag,
  type TableQuerySpec,
  type TableViewState,
  type View,
  type WorkflowEvent,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import type { RegionFlagSavedItemFragment } from "@/modules/saved-items/graphql/saved-items.gql.ts";
import {
  CLEAR_SAVED_VIEW,
  COPY_VIEWS_FAILURE,
  COPY_VIEWS_REQUEST,
  COPY_VIEWS_SUCCESS,
  CREATE_VIEW_FAILURE,
  CREATE_VIEW_REQUEST,
  CREATE_VIEW_SUCCESS,
  DELETE_SAVED_ITEM_SUCCESS,
  DELETE_VIEWS_FAILURE,
  LOAD_VIEW_FAILURE,
  LOAD_VIEW_REQUEST,
  LOAD_VIEW_SUCCESS,
} from "@/redux/action-types.ts";
import { BLAST_SUB_VIEW, DATA_VIEWER_MODE, ENTITY_TYPE, UNSPECIFIED_ENTITY_ID, VIEW } from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { buildCopyViewsMessage, buildSavedViewMessage } from "../../app-utils/alert.js";
import { SelMessage } from "../../messages/SelMessage.js";
import { notify } from "../../modules/notifications/notify.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { getRefGenomeFromSavedView, loadUserParams, updateUserParams } from "./auth.js";
import { type IDatasetViewerState } from "./dataset-viewer.js";
import { type IGenomeBrowserState, type IMutantGenome, type ISortableTrack } from "./genome-browser.js";
import { type IJobState } from "./job.js";
import {
  createTableSpecForOrganismParent,
  getTableDataSlice,
  type ITableColumn,
  type ITableData,
  sameTableQuerySpecRestrictions,
  sameTableSpec,
} from "./table-data.js";

/**
 * State definition
 */
export interface SavedViewState {
  isLoading: boolean;
  loadingViewId: number; // memorize the currently loaded viewId
  view: View;
}

export interface SaveViewParams {
  name: string;
  project: Project;
  shared: boolean;
  description: string;
  viewType: string;
  subViewType?: string;
  viewEntityLabel: string; // for making up the viewLabel when saving the view
  route: string;
  entityId: number; // for summary views
  entityType: string; // for summary views
  tableSpec?: InputTableQuerySpec; // for views that embed a specific table view
  viewId?: number;
  itemTagIds?: number[];
}

const initialState: SavedViewState = {
  view: null,
  loadingViewId: undefined,
  isLoading: false,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [CLEAR_SAVED_VIEW]: () => {
    // see SEL6-2508
    return initialState;
  },
  [LOAD_VIEW_REQUEST]: (state: SavedViewState, payload: { viewId: number }) => {
    return { ...state, isLoading: true, view: null, loadingViewId: payload.viewId };
  },
  [LOAD_VIEW_SUCCESS]: (state: SavedViewState, payload: View) => {
    return {
      ...state,
      isLoading: false,
      view: state.view?.id === payload.id ? state.view : payload, // since the action type "LOAD_VIEW_SUCCESS" is triggered again in order to reload the view data in some other redux modules, we protect this state from reference changes to prevent potential infinite loops (e.g. comments in SEL6-3496)
      loadingViewId: undefined,
    };
  },
  [LOAD_VIEW_FAILURE]: (state: SavedViewState) => {
    return { ...state, isLoading: false, loadingViewId: undefined };
  },
  // CREATE_VIEW_{REQUEST,SUCCESS,FAILURE} intentionally does not replace the view attribute of ISavedViewState
  //    Otherwise a "Save As" action from the table display of a View would replace the current view.
  //    That leaves nothing to do for CREATE_VIEW action.
});

/**
 * Action creators
 */

function initiateCreateViewRequest(payload: InputViewInput) {
  return {
    type: CREATE_VIEW_REQUEST,
    payload: payload,
  };
}

function createViewRequestSuccess(response: View) {
  return {
    type: CREATE_VIEW_SUCCESS,
    payload: response,
  };
}

export function extractTableViewData(table: ITableData): InputTableViewStateInput {
  if (!table) {
    return null;
  }
  const visibleColumns: InputColumnView[] =
    table.columns &&
    table.columns
      .filter((c) => !c.hidden)
      .map((c: ITableColumn) => ({ field: c.field, rowFilter: c.filterExpression, sort: c.sort }));
  return {
    tableQuerySpec: table.tableQuerySpec,
    columns: visibleColumns,
    rowIds: table.rowIds,
    selectedIds: table.selectionModel && table.selectionModel.selectedIds,
    totalRowCount: table.totalRowCount,
    filteredRowCount: table.filteredRowCount,
    expansionPath: table.expansionPath,
  };
}

function createViewInputParams(createViewParams: SaveViewParams, state: IAppState): InputViewInput {
  const viewStateInput: InputViewStateInput = {};

  let viewLabel: string = VIEW[createViewParams.viewType].title;

  const refGenomeId: number =
    state.auth.session?.params.referenceGenome !== UNSPECIFIED_ENTITY_ID
      ? state.auth.session?.params.referenceGenome
      : null;

  switch (createViewParams.viewType) {
    case VIEW.GENOME_VIEW.type:
      const genomeBrowser: IGenomeBrowserState = state.genomeBrowser;
      const variantTableData: ITableData = getTableDataSlice(
        state.tableData,
        createTableSpecForOrganismParent(ENTITY_TYPE.VARIANT),
      );
      const vcfVariantTableData: ITableData = getTableDataSlice(
        state.tableData,
        createTableSpecForOrganismParent(ENTITY_TYPE.VCF_VARIANT),
      );

      const validTracks: InputTrack[] = genomeBrowser.tracks
        .filter((track) => !track.hidden)
        .map((t: ISortableTrack) => {
          const inputTrack: InputTrack = {
            type: t.type,
            subTypeId: t.subTypeId,
            pinned: t.pinned,
            trackCustomization: t.trackCustomization,
          };
          return inputTrack;
        });

      const activeTableQuerySpec: TableQuerySpec = state.genomeBrowser.activeFeaturesTable || null;
      const currentTableState: InputTableViewStateInput = activeTableQuerySpec
        ? extractTableViewData(getTableDataSlice(state.tableData, activeTableQuerySpec))
        : null;

      viewStateInput.genomeView = {
        contigId: genomeBrowser.contig.id,
        genomeId: genomeBrowser.genome.id,
        range: genomeBrowser.range,
        tracks: validTracks,
        hideFeatureTable: genomeBrowser.featuresTableLayout.hidden,
        verticalLayout: genomeBrowser.featuresTableLayout.verticalLayout,
        showFlags: state.auth.session?.params.showFlagsTrack,
        useSpecificFlagsColoring: state.auth.session?.params.useSpecificFlagsColoring,
        condensedFlagsDisplay: state.auth.session?.params.condensedFlagsDisplay,
        showCommunityAnnotation: state.auth.session?.params.showCommunityAnnotations,
        headerWidth: genomeBrowser.layout.headerWidth,
        variantExpressionFilters: variantTableData?.filterExpression || vcfVariantTableData?.filterExpression,
        orderedMutantGenomes: genomeBrowser.selectedMutantGenomes.map((profile: IMutantGenome) => profile.id),
        refGenomeId,
        featureTableRestriction: state.tableData.rowRestriction,
        blastView: matchPath({ path: "/job/:id", end: false }, getCurrentRouteWithoutBasename())
          ? buildBlastJobViewStateInput(state)
          : undefined, // for SEL6-539
        tableView: currentTableState,
      };
      break;

    case VIEW.DATA_VIEW.type:
      const datasetViewer: IDatasetViewerState = state.datasetViewer;
      viewStateInput.datasetView = {
        selectedDataSeries: JSON.stringify(datasetViewer.selectedDataSeries),
        selectedAnnotations: JSON.stringify(
          datasetViewer.selectedAnnotations.map((sa: ColumnAnnotation) => {
            // CXR: Columns might contain characters which are incompatible with the DB character set.
            // Therefore we encode them.
            return { label: sa.name, conditionValues: sa.valueSet.map((s) => encodeURI(s)) };
          }),
        ),
        chartLayoutState: datasetViewer.chartLayout,
        datasetId: datasetViewer.dataset.id,
        showFilterPanel: datasetViewer.showFilterPanel,
        filteredOutAnnotations: JSON.stringify(
          Object.keys(datasetViewer.filteredOutAnnotations).map((fa: string) => ({
            ...datasetViewer.filteredOutAnnotations[fa],
            name: fa,
          })),
        ),
        splitPaneLayoutState: datasetViewer.splitPaneLayout,
        groupMode: datasetViewer.mode === DATA_VIEWER_MODE.GROUP,
        chartSelection: datasetViewer.chartSelection,
        chartTableSelection: datasetViewer.chartTableSelectionModel.selectedIds as any, // expects an Array<string>
        groupTableViewState:
          datasetViewer.tableSelectionSpec &&
          extractTableViewData(getTableDataSlice(state.tableData, datasetViewer.tableSelectionSpec)),
        hideEmptyDataSeries: datasetViewer.hideEmptyDataSeries,
      };
      break;

    case VIEW.BLAST_JOB_VIEW.type:
      const jobState: IJobState = state.job;
      let suffix = "";
      switch (createViewParams.subViewType) {
        case BLAST_SUB_VIEW.SUMMARY:
          suffix = " Summary";
          break;
        case BLAST_SUB_VIEW.ALIGNMENTS:
          suffix = " Alignment";
          break;
      }
      viewLabel = jobState.job.tool && jobState.job.tool.toUpperCase() + suffix;
      viewStateInput.blastView = buildBlastJobViewStateInput(state);
      break;

    case VIEW.ENTITY_SUMMARY_VIEW.type:
      viewLabel = `${createViewParams.viewEntityLabel} Summary`;

      viewStateInput.summaryView = {
        id: createViewParams.entityId, // id and type are not used on the client side, only for validation purpose on middle tier (e.g. user group visibility)
        type: createViewParams.entityType,
        refGenomeId: createViewParams.entityType === ENTITY_TYPE.MUTATION && refGenomeId ? refGenomeId : undefined,
        blastView: matchPath({ path: "/job/:id", end: false }, getCurrentRouteWithoutBasename())
          ? buildBlastJobViewStateInput(state)
          : undefined, // for SEL6-539
      };
      break;

    case VIEW.TABLE_VIEW.type:
      viewLabel = `${createViewParams.viewEntityLabel} Table`;
      viewStateInput.tableView = extractTableViewData(getTableDataSlice(state.tableData, createViewParams.tableSpec));
      break;

    case VIEW.CONTENT_BROWSER_VIEW.type:
      viewStateInput.contentBrowserView = {
        // save the content browser state
        featureType: state.contentBrowser.featureType,
        groupByAttribute: state.contentBrowser.groupByAttribute,
        selectedEntry: state.contentBrowser.selectedEntry,
        // and save the table view data
        tableView: extractTableViewData(getTableDataSlice(state.tableData, createViewParams.tableSpec)),
      };
      break;
  }

  return {
    id: createViewParams.viewId,
    projectId: createViewParams.project.id,
    shared: createViewParams.shared,
    description: createViewParams.description,
    name: createViewParams.name,
    // view type specific data
    viewType: createViewParams.viewType,
    viewLabel: viewLabel,
    params: createViewParams.route,
    state: viewStateInput,
    itemTagIds: createViewParams.itemTagIds,
  };
}

function buildBlastJobViewStateInput(state): InputBlastViewStateInput {
  return {
    jobId: state.job.job.id,
    colorMismatchesOnly: state.blast.colorMismatchesOnly,
  };
}

export function validateSingleViewAgainstProjectUserGroup(payload: SaveViewParams) {
  return (_dispatch, getState, webApiClient: WebApiClient): Promise<boolean> => {
    const state: IAppState = getState();
    const viewInputParams: InputViewInput = createViewInputParams(payload, state);

    return webApiClient.validateSingleViewAgainstProjectUserGroup(viewInputParams);
  };
}

export function saveView(payload: SaveViewParams, isUpdate?: boolean) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<View> => {
    const state: IAppState = getState();
    const viewInputParams: InputViewInput = createViewInputParams(payload, state);

    dispatch(initiateCreateViewRequest(payload));
    return webApiClient
      .saveView(viewInputParams)
      .then((view: View) => {
        dispatch(createViewRequestSuccess(view));

        // Reload current lastSaveTarget from configuration
        dispatch(loadUserParams());

        notify.info(
          {
            title: selMessage({ id: "view.save.successTitle" }),
            children: buildSavedViewMessage(view.name, payload.project, isUpdate),
          },
          { autoClose: 10000 },
        );
        return view;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_VIEW_FAILURE,
          alertTitle: "Saving View Failed",
          alertMessage: `The view "${payload.name}" could not be created in our database`,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

// when requesting a view we add an entry in the following object with [viewId: Promise]
// success and failure requests would delete this entry
// loading of same view would first check in this map and return the promise if exists
// (do not store this variable in redux since it is not recommended to have complex objects there, and this is not necessary anyway)
// (where else should we apply this pattern ?)

// TODO in theory no need for state.savedView.loadingViewId any longer
const pendingViewFetchingPromises: Record<number, Promise<View>> = {}; // map by viewId

export function loadView(viewId: number, preventReloadOfSavedView?: boolean) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<View> => {
    const state: IAppState = getState();
    if (!viewId) {
      return Promise.resolve(null);
    }

    // return already existing promise corresponding to the same view request
    if (pendingViewFetchingPromises[viewId]) {
      return pendingViewFetchingPromises[viewId];
    }

    if (state.savedView.view && state.savedView.view.id === viewId) {
      // TODO apply this technique to other cases e.g. for replacing savedViewAppliesToTableQuerySpec ? would allow to spare some complexity in component initialization
      if (!viewApplicableToCurrentLocation(getCurrentRouteWithoutBasename(), state.savedView.view)) {
        return Promise.resolve(null);
      }

      // in case the view has already been loaded, we do not ALWAYS want to apply again the view to the store
      // e.g. SEL6-1961: if the ref genome is not the same as when the view was resurrected, we do not want to reload again otherwise the row ids/selection model would mismatch !
      //if (!tableSpec || savedViewAppliesToTableQuerySpecRestrictions(state.savedView.view, tableSpec)) {
      if (!preventReloadOfSavedView) {
        // [SEL7-632] in the case of the features table of the genome browser, we do not want the saved view to be reloaded each time one track/tab is initialized
        dispatch(fetchViewRequestSuccess(state.savedView.view)); // reload eventually the table (embedded in the view) that might have been reset in between (see SEL5-2062)
      }
      return Promise.resolve(state.savedView.view);
    }

    dispatch(fetchViewRequest(viewId));
    // store the Promise as an entry of the pending view requests promises
    pendingViewFetchingPromises[viewId] = webApiClient
      .fetchView(viewId)
      .then((view: View) => {
        return viewApplicableToCurrentLocation(getCurrentRouteWithoutBasename(), view) ? view : null;
      })
      .then(
        (view: View) => {
          if (view) {
            dispatch(fetchViewRequestSuccess(view));
          }
          return view;
        },
        (rejected) => {
          delete pendingViewFetchingPromises[viewId];
          throw rejected;
        },
      )
      .then((view: View) => {
        if (view) {
          const userParams: Partial<InputUserParamsInput> = {};

          const viewRefGenomeId: number = getRefGenomeFromSavedView(view);
          if (viewRefGenomeId) {
            userParams.referenceGenome = viewRefGenomeId;
          }

          // resurrect the user preferences if they were stored as part of the saved view
          if (view.viewType === VIEW.GENOME_VIEW.type) {
            const genomeViewState: GenomeViewState = view.state.genomeView;
            userParams.showCommunityAnnotations = genomeViewState.showCommunityAnnotation;
            userParams.showFlagsTrack = genomeViewState.showFlags;
            userParams.useSpecificFlagsColoring = genomeViewState.useSpecificFlagsColoring;
            userParams.condensedFlagsDisplay = genomeViewState.condensedFlagsDisplay;
          }

          if (Object.keys(userParams).length) {
            dispatch(updateUserParams(userParams));
          }
        }
        return view;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_VIEW_FAILURE,
          alertTitle: "Fetching of View Failed",
          alertMessage: `Error while fetching saved view ${viewId}`,
        }),
      );

    return pendingViewFetchingPromises[viewId];
  };
}

function fetchViewRequest(viewId: number) {
  return {
    type: LOAD_VIEW_REQUEST,
    payload: { viewId: viewId },
  };
}

function fetchViewRequestSuccess(view: View) {
  delete pendingViewFetchingPromises[view.id];
  return {
    type: LOAD_VIEW_SUCCESS,
    payload: view,
  };
}

export function getViewRoute(view: View): To {
  return { pathname: view.params, search: `?viewid=${view.id}` };
}

export function getParentRoute(parent: Parent): To {
  return { pathname: `/parent/${parent.id}` };
}

export function getFlagRoute(flag: RegionFlag | RegionFlagSavedItemFragment): To {
  return { pathname: `/visualizer/genome`, search: `?flag=${flag.id}` };
}

export function getCoordListRoute(coordinateList: CoordinateList): To {
  return {
    pathname: `/organism/${coordinateList.organism.id}/genome`,
    search: `?add_tracks=${ENTITY_TYPE.COORDINATE}&coordLists=${coordinateList.id}&showGrid=true`,
  };
}

export function getGroupRoute(group: Group): To {
  return { pathname: `/group/${group.id}` };
}

export function getPlaybookEventRoute(playbookEvent: PlaybookEvent): To {
  return { pathname: `/event/${playbookEvent.id}` };
}

export function getWorkflowEventRoute(workflowEvent: WorkflowEvent): To {
  return { pathname: `/processor/session/${workflowEvent.workflowEventId}` };
}

export function getViewTitle(view: View): string {
  let res: string;
  if (view.viewLabel) {
    res = view.viewLabel;
  } else {
    res = VIEW[view.viewType] ? VIEW[view.viewType].title : "";
  }
  return res.replace("Blast", "BLAST");
}

export function deleteViews(viewIds: number[]) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<View[]> => {
    return webApiClient
      .deleteViewList(viewIds)
      .then((response: View[]) => {
        // dispatch(push("/home"));
        dispatch({
          type: DELETE_SAVED_ITEM_SUCCESS,
          payload: {
            deletedIds: viewIds,
            type: ENTITY_TYPE.VIEW,
          },
        });

        notify.info({
          title: selMessage({ id: "view.delete.successTitle", values: { viewCount: viewIds.length } }),
          children: SelMessage({ id: "view.delete.successMessage", values: { viewCount: viewIds.length } }),
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_VIEWS_FAILURE,
          alertTitle: selMessage({ id: "view.delete.errorTitle" }),
          alertMessage: selMessage({ id: "view.delete.errorMessage", values: { viewsCount: viewIds.length } }),
        }),
      );
  };
}

function initiateCopyViewsRequest(viewIds: number[], project: Project, isPrivate: boolean) {
  return {
    type: COPY_VIEWS_REQUEST,
    payload: {
      viewIds,
      project,
      isPrivate,
    },
  };
}

function copyViewsRequestSuccess(response: boolean) {
  return {
    type: COPY_VIEWS_SUCCESS,
    payload: response,
  };
}

export function copyViews(viewIds: number[], project: Project, isPrivate: boolean) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    dispatch(initiateCopyViewsRequest(viewIds, project.id, isPrivate));
    return webApiClient
      .copyViews(viewIds, project.id, isPrivate)
      .then((response: boolean) => {
        dispatch(copyViewsRequestSuccess(response));

        // Reload current lastSaveTarget from configuration
        dispatch(loadUserParams());

        notify.info(
          {
            title: SelMessage({ id: "view.copy.successTitle", values: { viewCount: viewIds.length } }),
            children: buildCopyViewsMessage(viewIds.length, project),
          },
          { autoClose: 10000 },
        );

        return true;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: COPY_VIEWS_FAILURE,
          alertTitle: "Copy Views Failed",
          alertMessage: `The views could not be copied in our database`,
        }),
      );
  };
}

export function validateSavedViewsAgainstProjectUserGroup(projectId: number, viewIds: number[]) {
  return (_dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient.validateSavedViewsAgainstProjectUserGroup(projectId, viewIds);
  };
}

/**************** Utils ******************/

// [SEL6-3497] if the saved view is already loaded but the current path does not correspond, then we do not want to apply the view data again
// TODO apply this technique to other cases e.g. for replacing savedViewAppliesToTableQuerySpec ? would allow to spare some complexity in component initialization
export function viewApplicableToCurrentLocation(currentLocation: string, view: View): boolean {
  return !!matchPath({ path: view.params, end: false }, currentLocation);
}

//  when loading the saved view:  saved view and table query spec should match (restrictions will be loaded as part of the saved view)
export function savedViewAppliesToTableQuerySpec(view: View, tableSpec?: InputTableQuerySpec): boolean {
  // the viewId param might be coming from another table view (in which case we get different table specs)
  if (view.viewType === VIEW.TABLE_VIEW.type) {
    const tableViewState: TableViewState = view.state.tableView;
    return tableViewState && sameTableSpec(tableViewState.tableQuerySpec, tableSpec);
  }
  if (view.viewType === VIEW.GENOME_VIEW.type) {
    const tableViewState: TableViewState = view.state.genomeView.tableView;
    return tableViewState && sameTableSpec(tableViewState.tableQuerySpec, tableSpec);
  }
  return view.viewType === VIEW.CONTENT_BROWSER_VIEW.type;
}

// when sustaining the saved view (view already loaded, we are in the context of the view): saved view and table query spec should match EXACTLY together with the restrictions
// otherwise we might end up with a mismatch between the row ids/selection model and the actual viewed rows (e.g. SEL6-1961)
export function savedViewAppliesToTableQuerySpecRestrictions(view: View, tableSpec: InputTableQuerySpec): boolean {
  // if the table query restrictions are not the same (e.g. ref genome changed, or other restriction changed) then the row ids and such (e.g. selection model, see comments of SEL6-1961) are invalid and should not be loaded
  const tableViewState: TableViewState = view.state.tableView;
  if (view.viewType === VIEW.TABLE_VIEW.type) {
    return (
      tableViewState &&
      sameTableSpec(tableViewState.tableQuerySpec, tableSpec) &&
      sameTableQuerySpecRestrictions(tableViewState.tableQuerySpec.restrictions, tableSpec.restrictions)
    );
  }
  return savedViewAppliesToTableQuerySpec(view, tableSpec);
}
